import React, { useState, useEffect, useRef } from "react";

import { useDispatch, useSelector } from "react-redux";
import { goBack, push } from "connected-react-router";

import ROUTES from "../../../../routes";
import {
  BoxSeparator,
  ContentBox,
  Page,
  TitleBox,
  Button,
  ForbiddenSection,
  Confirm,
} from "../../../../components";
import {
  business as businessActions,
  user as userActions,
} from "../../../../actions";
import { Language } from "../../../../utils";

import {
  Grid,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import { toast } from "react-toastify";

import makeClasses from "./styles";
import { Policies } from "../../../../utils/Policies";
import { Alert, AlertTitle } from "@material-ui/lab";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import clsx from "clsx";

const AddPosRequestScreen = () => {
  const dispatch = useDispatch();
  const classes = makeClasses();
  const SCROLL_THRESHOLD = 50;
  const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
  const [posTypes, setPosTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [inputPosType, setInputPosType] = useState(0);
  const [inputBranch, setInputBranch] = useState(0);
  const [inputBranchAddress, setInputBranchAddress] = useState("");
  const [pickupCenters, setPickupCenters] = useState([]);
  const [pickupCentersPagination, setPickupCentersPagination] = useState({
    page: 0,
    hasNext: false,
    loading: false,
  });
  const [inputPickupCenter, setPickupCenter] = useState(0);
  const [forbiddenSection, setForbbidenSection] = useState(false);
  const { policies, authTime, languageTexts } = useSelector((state) => ({
    policies: state.user.userData.policies || [],
    authTime: state.user.userData.authTime || Date.now(),
    languageTexts: state.language.texts || {},
  }));
  const i18n = Language(languageTexts);
  const deliveryTypeOptions = [
    { label: i18n.get("AddPosRequest.InputPosDeliveryTypeOption"), value: 1 },
    {
      label: i18n.get("AddPosRequest.InputPosDeliveryTypeOptionShipping"),
      value: "branch",
    },
    {
      label: i18n.get("AddPosRequest.InputPosDeliveryTypeOptionPickup"),
      value: "pickup",
    },
  ];
  const [deliveryType, setDeliveryType] = useState(1);
  const canSubmitForm =
    inputPosType > 0 &&
    inputBranch &&
    inputBranch !== 0 &&
    inputBranchAddress?.id > 0 &&
    (deliveryType === "branch" ||
      (deliveryType === "pickup" && inputPickupCenter));

  useEffect(() => {
    window.scrollTo(0, 0);

    const exiredSession =
      Math.floor((new Date() - new Date(authTime * 1000)) / (1000 * 60)) >= 15;
    if (exiredSession) {
      return _toggleSessionExpiredModal(true);
    }

    if (policies && policies.includes(Policies.types.SENSITIVE_DATA)) {
      dispatch(
        userActions.getProfileData(null, (profile, branches) => {
          const hasRUT =
            profile.merchantIdentifier &&
            profile.merchantIdentifier.merchantDocumentType === "RUT";

          setBranches(branches);

          if (branches.length === 1) {
            setInputBranch(branches[0].traceId);
          }

          dispatch(
            userActions.getPosTypes(
              hasRUT ? "LEGAL_PERSON" : "PHYSICAL_PERSON",
              (data) => {
                setPosTypes(data);

                if (data.length === 1) {
                  setInputPosType(data[0].id);
                }
              }
            )
          );
        })
      );
    } else {
      setForbbidenSection(true);
    }
  }, [policies, authTime]);

  useEffect(() => {
    if (inputBranch) {
      dispatch(
        businessActions.getBranchAddress(inputBranch, (branchAddress) => {
          setInputBranchAddress(branchAddress);
        })
      );
    } else {
      setInputBranchAddress("");
    }
  }, [inputBranch]);

  const _goBack = () => {
    dispatch(goBack());
  };

  const _goToPendingPosRequests = () => {
    dispatch(
      push({
        pathname: ROUTES.MANAGE_POS_REQUESTS.path,
        search: "?view=0",
      })
    );
  };

  const _submitForm = () => {
    if (!canSubmitForm) {
      return false;
    }

    const addressToSend =
      deliveryType === "branch"
        ? inputBranchAddress?.id
        : inputPickupCenter.address.id;

    dispatch(
      userActions.addPosRequest(
        {
          physicalPOSTypeId: inputPosType,
          addressId: addressToSend,
          branchTraceId: inputBranch,
        },
        (success) => {
          if (success) {
            toast.success(i18n.get("AddPosRequest.AddPosRequestSuccess"));
            setTimeout(() => {
              _goToPendingPosRequests();
            }, 2000);
          } else {
            toast.error(i18n.get("AddPosRequest.AddPosRequestError"));
          }
        }
      )
    );
  };

  const _toggleSessionExpiredModal = (value) => {
    setShowSessionExpiredModal(value);
  };

  const _handleDeliveryTypeChange = (event) => {
    const value = event.target.value;
    setDeliveryType(value);
    setPickupCenter(0);
    if (value === "pickup") {
      setPickupCentersPagination({
        page: 0,
        hasNext: false,
        loading: false,
      });
      loadPickupCenters(1, true);
    }
  };

  const loadPickupCenters = (page = 1, reset = false) => {
    if (pickupCentersPagination.loading) return;

    const requestedPage = page;

    setPickupCentersPagination((prev) => ({
      ...prev,
      loading: true,
    }));

    dispatch(
      businessActions.getPickupCenters((data) => {
        const newElements = data?.elements || [];
        setPickupCenters((prev) =>
          reset ? newElements : [...prev, ...newElements]
        );
        setPickupCentersPagination({
          page: requestedPage,
          hasNext: data?.hasNext || false,
          loading: false,
        });
      }, page)
    );
  };

  const handlePickupCenterMenuScroll = (event) => {
    const { target } = event;
    if (
      pickupCentersPagination.hasNext &&
      !pickupCentersPagination.loading &&
      target.scrollHeight - target.scrollTop - target.clientHeight <
        SCROLL_THRESHOLD
    ) {
      const nextPage =
        pickupCentersPagination.page >= 1
          ? pickupCentersPagination.page + 1
          : 2;
      loadPickupCenters(nextPage);
    }
  };

  const _logOut = () => {
    dispatch(userActions.logOut());
  };

  const renderAddPosRequestForm = () => {
    return (
      <Grid
        container
        justify="center"
        alignItems="center"
        className={classes.userFormWrapper}>
        <Grid item xs={12} sm={12} md={12} lg={12}>
          <form
            layout="vertical"
            onSubmit={_submitForm}
            className={classes.userForm}>
            <Grid container spacing={5}>
              <Grid item xs={12} sm={12} md={12}>
                <Grid container spacing={4}>
                  <Grid item xs={12} sm={12} md={6}>
                    <React.Fragment>
                      <FormControl fullWidth>
                        <InputLabel shrink className={classes.formSelectLabel}>
                          {i18n.get("AddPosRequest.InputPosTypeLabel")}
                        </InputLabel>
                        <Select
                          className={classes.formSelect}
                          onChange={(e) => setInputPosType(e.target.value)}
                          value={inputPosType}>
                          <MenuItem
                            value={0}
                            className={classes.formSelectOption}>
                            {i18n.get("AddPosRequest.DefaultPosTypeOption")}
                          </MenuItem>
                          {posTypes.map((item, index) => (
                            <MenuItem
                              key={index}
                              value={item.id}
                              className={classes.formSelectOption}>
                              {item.name}
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText
                          className={classes.formSelectHelperText}>
                          {posTypes.find((p) => p.id === inputPosType)
                            ?.description || ""}
                        </FormHelperText>
                      </FormControl>
                    </React.Fragment>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <InputLabel className={classes.formSelectLabel} shrink>
                      {i18n.get("AddPosRequest.InputBranchLabel")}
                    </InputLabel>
                    <Select
                      className={classes.formSelect}
                      onChange={(e) => setInputBranch(e.target.value)}
                      value={inputBranch}>
                      <MenuItem value={0} className={classes.formSelectOption}>
                        {i18n.get("AddPosRequest.DefaultBranchOption")}
                      </MenuItem>
                      {branches.map((branch, index) => (
                        <MenuItem
                          key={index}
                          value={branch.traceId}
                          className={classes.formSelectOption}>
                          {branch.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  <Grid item xs={12} sm={12} md={6}>
                    <InputLabel className={classes.formSelectLabel} shrink>
                      {i18n.get("AddPosRequest.InputPosDeliveryTypeLabel")}
                    </InputLabel>
                    <Select
                      className={classes.formSelect}
                      onChange={_handleDeliveryTypeChange}
                      value={deliveryType}>
                      {deliveryTypeOptions.map((deliveryType, index) => (
                        <MenuItem
                          key={index}
                          value={deliveryType.value}
                          className={classes.formSelectOption}>
                          {deliveryType.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </Grid>
                  {deliveryType === "branch" && (
                    <Grid item xs={12} sm={12} md={6}>
                      <TextField
                        InputLabelProps={{
                          shrink: true,
                        }}
                        className={classes.formInput}
                        label={i18n.get("AddPosRequest.InputPosAddressLabel")}
                        placeholder={i18n.get(
                          "AddPosRequest.InputPosAddressPlaceholder"
                        )}
                        disabled
                        value={
                          inputBranchAddress
                            ? `${inputBranchAddress?.city?.name}, ${
                                inputBranchAddress?.street || ""
                              } ${inputBranchAddress?.number || ""}`.trim()
                            : "-"
                        }
                      />
                    </Grid>
                  )}
                  {deliveryType === "pickup" && (
                    <Grid item xs={12} sm={12} md={6}>
                      <InputLabel className={classes.formSelectLabel} shrink>
                        {i18n.get("AddPosRequest.InputPickupCenterLabel")}
                      </InputLabel>
                      <Select
                        className={classes.formSelect}
                        onChange={(e) => setPickupCenter(e.target.value)}
                        value={inputPickupCenter}
                        MenuProps={{
                          PaperProps: {
                            onScroll: handlePickupCenterMenuScroll,
                            style: { maxHeight: 300 },
                          },
                        }}>
                        <MenuItem
                          value={0}
                          className={classes.formSelectOption}>
                          {i18n.get(
                            "AddPosRequest.InputPickupCenterDefaultOption"
                          )}
                        </MenuItem>
                        {pickupCenters.map((agency, index) => (
                          <MenuItem
                            key={index}
                            value={agency}
                            className={classes.formSelectOption}>
                            {`${agency.name} (${agency.address.city.name}, ${agency.address.street} ${agency.address.number})`}
                          </MenuItem>
                        ))}
                        {pickupCentersPagination.loading && (
                          <MenuItem
                            disabled
                            className={classes.formSelectOption}>
                            Cargando más centros...
                          </MenuItem>
                        )}
                      </Select>
                    </Grid>
                  )}
                </Grid>
              </Grid>
              {deliveryType === "pickup" && inputPickupCenter !== 0 && (
                <Grid item xs={12} sm={12} md={12}>
                  <Alert
                    severity="info"
                    icon={false}
                    className={clsx(
                      classes.alert,
                      classes.alertInfo,
                      classes.posDescriptionAlert
                    )}>
                    <div>
                      <InfoOutlinedIcon
                        className={clsx(
                          classes.alertIcon,
                          classes.posDescriptionAlertIcon
                        )}
                      />
                    </div>
                    <div
                      className={clsx(
                        classes.alertMessage,
                        classes.posDescriptionAlertMessage
                      )}>
                      <AlertTitle
                        className={clsx(
                          classes.alertTitle,
                          classes.posDescriptionAlertTitle
                        )}>
                        {i18n.get("AddPosRequest.PickupCenterAlertTitle")}
                      </AlertTitle>
                      {inputPickupCenter.description}
                    </div>
                  </Alert>
                </Grid>
              )}
            </Grid>
          </form>
        </Grid>
      </Grid>
    );
  };

  if (forbiddenSection) {
    return (
      <Page
        withHeader
        withSidebar
        withHeaderTitle={i18n.get("AddPosRequest.Title")}
        withActivePage={ROUTES.MANAGE_POS_REQUESTS.id}
        withBanner={false}>
        <ForbiddenSection />
      </Page>
    );
  }

  return (
    <Page
      withHeader
      withSidebar
      withHeaderTitle={i18n.get("AddPosRequest.Title")}
      withActivePage={ROUTES.MANAGE_POS_REQUESTS.id}>
      <TitleBox
        title={i18n.get("AddPosRequest.BoxTitle")}
        back
        backTitle={i18n.get("AddPosRequest.BoxBackButton")}
        onBackClick={_goBack}
        backButtonVariant="outlined"
      />
      <BoxSeparator />
      <ContentBox title={i18n.get("AddPosRequest.BoxContentTitle")} titleBold>
        {renderAddPosRequestForm()}
        <div>
          <div className={classes.contentActionsWrapper}>
            <Button variant="outlined" onClick={_goBack}>
              {i18n.get("AddPosRequest.CancelButton")}
            </Button>
            <div className={classes.contentActionSeparator} />
            <Button disabled={!canSubmitForm} onClick={_submitForm}>
              {i18n.get("AddPosRequest.ContinueButton")}
            </Button>
          </div>
        </div>
      </ContentBox>
      <Confirm
        open={showSessionExpiredModal}
        onClose={_goBack}
        title={i18n.get("AddPosRequest.ExpiredSessionDialogTitle")}
        onConfirm={_logOut}
        confirmText={i18n.get(
          "AddPosRequest.ExpiredSessionDialogActionConfirm"
        )}
        cancelText={i18n.get("AddPosRequest.ExpiredSessionDialogActionCancel")}
        onCancel={_goBack}>
        <Alert
          severity="info"
          icon={false}
          className={clsx(classes.alert, classes.alertInfo)}>
          <div>
            <InfoOutlinedIcon className={classes.alertIcon} />
          </div>
          <div className={classes.alertMessage}>
            <AlertTitle className={classes.alertTitle}>
              {i18n.get("AddPosRequest.ExpiredSessionDialogContentTitle")}
            </AlertTitle>
            {i18n.get("AddPosRequest.ExpiredSessionDialogContentMessage")}
          </div>
        </Alert>
      </Confirm>
    </Page>
  );
};

AddPosRequestScreen.id = "com.Handy.AddPosRequest";

export default AddPosRequestScreen;
